import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import * as z from 'zod'

import {OS} from '@github-ui/get-os'
import {ContentfulDownloadOptionsSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/downloadOptions'
import {LinkSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/link'
import {PrimerComponentLogoSuiteSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentLogoSuite'
import {PrimerComponentTestimonialSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentTestimonial'
import type {RichText} from '@github-ui/swp-core/schemas/contentful/richText'

import type {BrandPageContent} from '../../../brand/components/BrandPage/definition'
import {
  BrandContentError,
  findByIdWithSchema,
  GenericAssetSchema,
  GenericContentSchema,
  GenericCTAGroupSchema,
  GenericGroupSchema,
  parseEntries,
  requireById,
  type GenericContent,
} from '../../../brand/lib/types/contentful'
import type {CopyData} from '../Index.data'
import type {Cta, DownloadMenu, SignUpField, ThemedVisual} from '../Index.types'
import {SIGN_UP_URL} from './config'
import type {homePage, ShowcaseContent} from './content'
import {parseTimestampToSeconds} from './time'

const PlatformSchema = z.enum(OS)

function required<T>(value: T | undefined, name: string): T {
  if (value === undefined) throw new BrandContentError(`Homepage: missing ${name}`)
  return value
}

function string(value: string | undefined, name: string): string {
  const result = required(value, name)
  if (!result.trim()) throw new BrandContentError(`Homepage: empty ${name}`)
  return result
}

function plainText(value: RichText | undefined, name: string) {
  return string(documentToPlainTextString(required(value, name)), name)
}

function url(value: string): string {
  const trimmed = string(value, 'URL').trim()
  const parsed = new URL(trimmed, 'https://github.com')
  if (!['https:', 'http:'].includes(parsed.protocol)) throw new BrandContentError(`Homepage: unsupported URL ${value}`)
  return trimmed.startsWith('//') || URL.canParse(trimmed) ? parsed.href : trimmed
}

function toCta(value: unknown): Cta {
  const {fields} = LinkSchema.parse(value)
  return {label: string(fields.text, 'link text'), url: url(fields.href)}
}

function toDownloadMenu(value: unknown): DownloadMenu {
  const {fields} = ContentfulDownloadOptionsSchema.parse(value)
  return {
    menuLabel: fields.menuLabel,
    actions: fields.options.map(option => ({
      id: option.sys.id,
      platform: PlatformSchema.parse(option.fields.platform),
      architecture: option.fields.architecture,
      label: option.fields.link.fields.text,
      url: url(option.fields.link.fields.href),
    })),
  }
}

function toVisual(entry: GenericContent): ThemedVisual {
  const light = requireById(entry.fields.media, 'light', GenericAssetSchema)
  const dark = findByIdWithSchema(entry.fields.media, 'dark', GenericAssetSchema)
  return {
    url: {
      light: url(light.fields.asset.fields.file.url),
      ...(dark ? {dark: url(dark.fields.asset.fields.file.url)} : {}),
    },
    alt: light.fields.description ?? '',
  }
}

function toActions(entry: GenericContent) {
  const {link, cta} = entry.fields
  if (link && cta) throw new BrandContentError('Homepage: content has both a direct link and a CTA group')
  if (link) return {cta1: toCta(link)}
  const group = GenericCTAGroupSchema.parse(required(cta, 'CTA group'))
  return {cta1: toCta(group.fields.primary), ...(group.fields.secondary ? {cta2: toCta(group.fields.secondary)} : {})}
}

function toStat(entry: GenericContent) {
  return {
    value: string(entry.fields.heading, 'statistic value'),
    label: plainText(entry.fields.text, 'statistic label'),
  }
}

function toFeatureGrid(entry: GenericContent): CopyData['features']['items'][number] {
  return {
    title: string(entry.fields.heading, 'feature collection heading'),
    items: parseEntries(entry.fields.content, GenericContentSchema).map(item => {
      const icons = parseEntries(item.fields.content, GenericContentSchema)
      if (icons.length > 1 || icons.some(icon => icon.fields.id !== 'iconMarkGithub')) {
        throw new BrandContentError('Homepage: unsupported feature icon')
      }
      return {
        title: string(item.fields.heading, 'feature heading'),
        description: plainText(item.fields.text, 'feature description'),
        ...(icons.length ? {icon: 'mark-github' as const} : {}),
        visual: toVisual(item),
        ...toActions(item),
      }
    }),
  }
}

function toShowcase(entry: ShowcaseContent, labels: (id: string) => string): NonNullable<CopyData['showcase']> {
  const cta = GenericCTAGroupSchema.parse(required(entry.fields.cta, 'Showcase CTA group'))
  const primary = cta.fields.primary
  const light = required(
    entry.fields.media?.find(item => item.fields.id === 'light'),
    'Showcase light visual',
  )
  const mime = string(light.fields.asset.fields.file.contentType, 'Showcase asset content type')
  if (!mime.startsWith('video/') && !mime.startsWith('image/')) {
    throw new BrandContentError(`Homepage: unsupported Showcase asset content type ${mime}`)
  }
  return {
    title: string(entry.fields.heading, 'Showcase heading'),
    description: plainText(entry.fields.text, 'Showcase description'),
    cta1:
      primary.sys.contentType.sys.id === 'downloadOptions'
        ? {type: 'download', ...toDownloadMenu(primary)}
        : {type: 'link', ...toCta(primary)},
    cta2: toCta(required(cta.fields.secondary, 'Showcase secondary action')),
    visual: {...toVisual(entry), type: mime.startsWith('video/') ? 'video' : 'image'},
    a11y: {
      playButton: {play: labels('play'), pause: labels('pause'), replay: labels('replay')},
    },
  }
}

export const parseContentfulData = (content: BrandPageContent<typeof homePage>): CopyData => {
  const intro = required(content.intro, 'intro')
  const introDownloads = GenericCTAGroupSchema.parse(required(intro.fields.cta, 'Intro download CTA group'))
  const hero = required(content.hero, 'hero')
  const activity = required(content.dataMarquee, 'dataMarquee')
  const features = required(content.features, 'featuresSection')
  const banner = required(content.ctaBanner, 'ctaBanner')
  const testimonials = required(content.testimonials, 'testimonialsSection')
  const stories = required(content.customerStories, 'customerStories')
  const footer = required(content.footer, 'footerSection')
  const interfaceText = required(content.interfaceText, 'interfaceText').fields.content

  const accessibilityTitle = (id: string) =>
    plainText(requireById(interfaceText, id, GenericContentSchema).fields.text, id)
  const labels = (groupId: string) => {
    const group = requireById(interfaceText, groupId, GenericGroupSchema)
    return (id: string) =>
      string(requireById(group.fields.content, id, GenericContentSchema).fields.heading, `${groupId}.${id}`)
  }
  const heroLabels = labels('heroLabels')
  const activityLabels = labels('activityLabels')
  const testimonialLabels = labels('testimonialLabels')
  const signUpLabels = labels('signUpLabels')
  const signUp: SignUpField = {
    label: signUpLabels('emailLabel'),
    placeholder: signUpLabels('emailPlaceholder'),
    cta: {label: signUpLabels('buttonLabel'), url: SIGN_UP_URL},
  }
  const events = requireById(interfaceText, 'activityEvents', GenericGroupSchema)
  const eventTemplate = (id: string) => {
    const item = requireById(events.fields.content, id, GenericContentSchema)
    return {action: string(item.fields.heading, `${id}.action`), text: plainText(item.fields.text, `${id}.text`)}
  }

  const introAction = (id: string) => toCta(requireById(intro.fields.content, id, GenericContentSchema).fields.link)
  const eyebrow = findByIdWithSchema(intro.fields.content, 'eyebrowBanner', GenericContentSchema)
  const monaAlt = findByIdWithSchema(intro.fields.content, 'monaAlt', GenericContentSchema)
  const chapters = parseEntries(hero.fields.content, GenericContentSchema)
  if (chapters.length === 0) throw new BrandContentError('Homepage: Hero needs at least one chapter')
  const chapterTimestamps = hero.fields.media?.length
    ? chapters.map(item => parseTimestampToSeconds(plainText(item.fields.subheading, 'chapter start timestamp').trim()))
    : undefined
  if (
    chapterTimestamps?.some(
      (timestamp, index) => !Number.isFinite(timestamp) || timestamp <= (chapterTimestamps[index - 1] ?? -1),
    )
  ) {
    throw new BrandContentError('Homepage: Hero chapter start timestamps must be finite and strictly increasing')
  }
  if (features.fields.content.length !== 2) throw new BrandContentError('Homepage: expected two feature collections')
  const data = requireById(testimonials.fields.content, 'data', GenericContentSchema)
  const stats = requireById(data.fields.content, 'testimonialStats', GenericGroupSchema)
  const statisticEntries = z.tuple([GenericContentSchema, GenericContentSchema]).safeParse(stats.fields.content)
  if (!statisticEntries.success) {
    throw new BrandContentError(
      `Homepage: testimonialStats must contain exactly two statistics:\n${z.prettifyError(statisticEntries.error)}`,
    )
  }
  const quotes = parseEntries(
    data.fields.content?.filter(item => item.fields.id !== 'testimonialStats'),
    PrimerComponentTestimonialSchema,
  )
  const logoGroup = requireById(testimonials.fields.content, 'logoMarquee', GenericGroupSchema)
  const logoEntries = z.tuple([PrimerComponentLogoSuiteSchema]).safeParse(logoGroup.fields.content)
  if (!logoEntries.success) {
    throw new BrandContentError(
      `Homepage: logoMarquee must contain exactly one Logo Suite:\n${z.prettifyError(logoEntries.error)}`,
    )
  }
  const [logos] = logoEntries.data
  const monaFooter = requireById(footer.fields.content, 'monaFooter', GenericContentSchema)
  const community = requireById(footer.fields.content, 'communityFooter', GenericContentSchema)

  return {
    intro: {
      title: string(intro.fields.heading, 'intro heading'),
      monaHead: {alt: monaAlt?.fields.heading ?? ''},
      ...(eyebrow
        ? {
            eyebrowBanner: {
              image: toVisual(eyebrow),
              text: toCta(eyebrow.fields.link).label,
              url: toCta(eyebrow.fields.link).url,
            },
          }
        : {}),
      ctas: {
        trial: introAction('trial'),
        sales: introAction('sales'),
        downloadCta: introAction('downloadCta'),
        downloadMenu: toDownloadMenu(introDownloads.fields.primary),
        signUp,
      },
    },
    hero: {
      a11y: {title: accessibilityTitle('heroAccessibilityTitle')},
      heroSequence: {
        chaptersAltDesc: chapters.map(item => plainText(item.fields.text, 'chapter alternative description')),
        ...(chapterTimestamps ? {video: {url: toVisual(hero).url, chapterTimestamps}} : {}),
      },
      heroPagination: {
        items: chapters.map(item => string(item.fields.heading, 'chapter label')),
        a11y: {
          pagination: {
            title: heroLabels('paginationTitle'),
            previous: heroLabels('previous'),
            next: heroLabels('next'),
          },
          playButton: {play: heroLabels('play'), pause: heroLabels('pause'), replay: heroLabels('replay')},
        },
      },
    },
    dataMarquee: {
      stats: parseEntries(activity.fields.content, GenericContentSchema).map(item => ({data: toStat(item)})),
      a11y: {
        title: accessibilityTitle('dataMarqueeAccessibilityTitle'),
        play: activityLabels('play'),
        pause: activityLabels('pause'),
      },
      copy: {
        events: {
          labelTemplate: {
            pull_request_opened: eventTemplate('pullRequestOpened'),
            pull_request_merged: eventTemplate('pullRequestMerged'),
            repository_forked: eventTemplate('repositoryForked'),
            repository_starred: eventTemplate('repositoryStarred'),
            push: eventTemplate('push'),
          },
        },
      },
    },
    ...(content.showcase ? {showcase: toShowcase(content.showcase, labels('showcaseLabels'))} : {}),
    features: {
      items: [
        toFeatureGrid(requireById(features.fields.content, 'platformFeatures', GenericContentSchema)),
        toFeatureGrid(requireById(features.fields.content, 'moreFeatures', GenericContentSchema)),
      ],
    },
    ctaBanner: {ctaBanner: {title: string(banner.fields.heading, 'CTA banner heading'), cta: toActions(banner).cta1}},
    testimonials: {
      data: {
        items: quotes.map(item => {
          const author = required(item.fields.author, 'testimonial author')
          const avatar = required(author.fields.photo, 'testimonial author photo')
          return {
            quote: plainText(item.fields.quote, 'testimonial quote'),
            author: {
              name: string(author.fields.fullName, 'author name'),
              position: string(author.fields.position, 'author position'),
              avatar: {url: url(avatar.fields.file.url), alt: avatar.fields.description ?? ''},
            },
          }
        }),
        stats: [toStat(statisticEntries.data[0]), toStat(statisticEntries.data[1])],
        a11y: {
          title: accessibilityTitle('testimonialsDataAccessibilityTitle'),
          testimonialGroup: {
            controlsPrev: testimonialLabels('controlsPrev'),
            controlsNext: testimonialLabels('controlsNext'),
            controlsNavigation: testimonialLabels('controlsNavigation'),
            carouselRoleDescription: testimonialLabels('carouselRoleDescription'),
            slideRoleDescription: testimonialLabels('slideRoleDescription'),
            indicator: testimonialLabels('indicator'),
            slide: testimonialLabels('slide'),
            announcement: testimonialLabels('announcement'),
          },
        },
      },
      logoMarquee: {
        logos: logos.fields.logos,
        a11y: {title: string(logos.fields.heading, 'logo marquee accessible title')},
      },
    },
    customerStories: {
      a11y: {title: accessibilityTitle('customerStoriesAccessibilityTitle')},
      items: parseEntries(stories.fields.content, GenericContentSchema).map(item => ({
        description: string(item.fields.heading, 'customer story description'),
        logo: toVisual(item),
        cta: toCta(item.fields.link),
      })),
    },
    footer: {
      monaFooter: {
        repos: {
          a11y: {title: accessibilityTitle('monaFooterReposAccessibilityTitle')},
          starsTemplate: plainText(monaFooter.fields.text, 'repository stars template'),
        },
      },
      communityFooter: {
        title: string(community.fields.heading, 'community heading'),
        label: plainText(community.fields.subheading, 'community label'),
        description: plainText(community.fields.text, 'community description'),
        signUp,
      },
    },
  }
}
